// profile-modal.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild, TemplateRef, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, Subject, takeUntil } from 'rxjs';

import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { ProfileService } from './profile.service';
import { AppStorageService } from 'src/app/services/app-storage.service';

import { ButtonModule } from 'src/app/shared/buttons/button.module';

const IMPORTS = [
    CommonModule, ReactiveFormsModule, ButtonModule
];

const RESET_EMAIL_COOLDOWN_MINUTES = 30;
const RESET_EMAIL_COOLDOWN_MS = RESET_EMAIL_COOLDOWN_MINUTES * 60 * 1000;
const COOLDOWN_TICK_MS = 1000;

export interface Account {
    _id?: string;
    id?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    email?: string;
    roles?: string[];
    permissions?: string[] | string;
}

@Component({
    selector: 'app-profile-modal',
    standalone: true,
    imports: [...IMPORTS],
    templateUrl: './profile-modal.component.html'
})
export class ProfileModalComponent implements OnInit, OnDestroy {

    @Input() account: Account = {};
    @Output() onUpdated = new EventEmitter<{ first_name: string; last_name: string }>();

    @ViewChild('profileModalBox') profileModalBox!: TemplateRef<unknown>;

    profileForm!: FormGroup;

    isSavingProfile = false;
    isSendingResetEmail = false;
    resetEmailCooldownEnd: number | null = null;
    cooldownTimer: ReturnType<typeof setInterval> | undefined;
    cooldownDisplay = '';

    private modalRef: NgbModalRef | null = null;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private modalService: NgbModal,
        private toastr: ToastrService,
        private userService: ProfileService,
        private appStorage: AppStorageService
    ) { }

    ngOnInit(): void {
        this.profileForm = this.fb.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            email: [{ value: '', disabled: true }]
        });
    }

    get firstNameControl() { return this.profileForm.get('firstName'); }
    get lastNameControl() { return this.profileForm.get('lastName'); }

    openModal(): void {
        this.profileForm.patchValue({
            firstName: this.account.first_name || '',
            lastName: this.account.last_name || '',
            email: this.account.email || ''
        });

        const storedCooldown = this.appStorage.getResetEmailCooldown();
        this.resetEmailCooldownEnd = storedCooldown ? parseInt(storedCooldown, 10) : null;

        if (this.resetEmailCooldownEnd && Date.now() < this.resetEmailCooldownEnd) {
            this.startCooldownTimer();
        } else {
            this.resetEmailCooldownEnd = null;
        }

        this.modalRef = this.modalService.open(this.profileModalBox, {
            backdrop: 'static',
            keyboard: false,
            centered: true,
            size: 'md',
            windowClass: 'modal-holder'
        });
    }

    closeModal(): void {
        this.modalRef?.dismiss();
    }

    saveProfile(): void {
        if (this.profileForm.invalid) {
            this.profileForm.markAllAsTouched();
            return;
        }

        this.isSavingProfile = true;
        const userId: string = this.account._id || this.account.id || this.appStorage.getUserId() || '';
        const { firstName, lastName }: { firstName: string; lastName: string } = this.profileForm.getRawValue();

        this.userService.updateProfile(userId, firstName, lastName)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isSavingProfile = false)
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Profile updated successfully', 'Success');
                    this.onUpdated.emit({ first_name: firstName, last_name: lastName });
                    this.modalRef?.close();
                },
                error: (err: unknown) => {
                    console.error('Failed to update profile', err);
                    this.toastr.error('Failed to update profile', 'Error');
                }
            });
    }

    sendPasswordResetEmail(): void {
        if (this.isSendingResetEmail) return;

        this.isSendingResetEmail = true;

        this.userService.sendReset(this.account.email ?? '')
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isSendingResetEmail = false)
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Password reset link has been sent to your email', 'Success');
                    this.resetEmailCooldownEnd = Date.now() + RESET_EMAIL_COOLDOWN_MS;
                    this.appStorage.setResetEmailCooldown(this.resetEmailCooldownEnd.toString());
                    this.startCooldownTimer();
                },
                error: (err: unknown) => {
                    console.error('Failed to send reset email', err);
                    this.toastr.error('Failed to send reset link. Please try again.', 'Error');
                }
            });
    }

    private startCooldownTimer(): void {
        if (this.cooldownTimer) clearInterval(this.cooldownTimer);

        this.updateCooldownDisplay();

        this.cooldownTimer = setInterval(() => {
            if (!this.resetEmailCooldownEnd || Date.now() >= this.resetEmailCooldownEnd) {
                this.resetEmailCooldownEnd = null;
                this.cooldownDisplay = '';
                this.appStorage.removeResetEmailCooldown();
                if (this.cooldownTimer) clearInterval(this.cooldownTimer);
                this.cooldownTimer = undefined;
                return;
            }
            this.updateCooldownDisplay();
        }, COOLDOWN_TICK_MS);
    }

    private updateCooldownDisplay(): void {
        if (!this.resetEmailCooldownEnd) {
            this.cooldownDisplay = '';
            return;
        }
        const remainingMs = this.resetEmailCooldownEnd - Date.now();
        const minutes = Math.floor(remainingMs / 60000);
        const seconds = Math.floor((remainingMs % 60000) / 1000);
        this.cooldownDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    resetCooldown(): void {
        if (this.cooldownTimer) {
            clearInterval(this.cooldownTimer);
            this.cooldownTimer = undefined;
        }
        this.resetEmailCooldownEnd = null;
        this.cooldownDisplay = '';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.cooldownTimer) clearInterval(this.cooldownTimer);
    }
}